import React, { useCallback, useEffect, useMemo } from 'react';
import classnames from 'classnames';

import { Field, Formik, FormikHelpers, FormikProps } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { ENetworkRequestStatus } from 'services/BackendApi';
import FluidButton from 'ui/FluidButton';
import { MapStayFormValidationSchema } from './formValidation';
import { GeneralModal } from 'ui/GeneralModal';
import SingleSelect from 'ui/SingleSelect';
import { RadioListBox } from 'ui/RadioListBox';
import { TextInput } from 'ui/TextInput';
import { createActions } from 'store/modules/liveRates/actions';
import {
  isMappableStaysTruncatedSelector,
  mappableStaysSelector,
  requestMappableStaysExternalGetSelector,
  requestMappableStaysInternalGetSelector,
  requestMappableStaysPatchSelector,
  unmappedHotelsSelector
} from 'store/modules/liveRates/selectors';
import { ELiveRatesExternalSystem } from 'services/BackendApi/types/LiveRatesInternal';
import { LoadingBar } from 'ui/NetworkStatusBar';
import { useSupplierConfig } from 'hooks/useSupplierConfig';

export interface IMapStayModalProps {
  onCancel: () => void;
}

export interface IMapStayFormValues {
  stayUuid: string | null;
  externalStayUuid: string | null;
  externalSupplier: ELiveRatesExternalSystem | null;
  termFilter: string;
}

export const MapStayModal = (props: IMapStayModalProps) => {
  const { onCancel } = props;

  const dispatch = useDispatch();
  
  const { supplierConfig } = useSupplierConfig();
  
  const supplierOptions = useMemo(() =>
    supplierConfig
      .filter(x => x.enabled)
      .map(x => ({
        value: x.externalSystem,
        label: x.name
      })),
    supplierConfig
  );

  const unmappedHotels = useSelector(unmappedHotelsSelector);
  const stayOptions = unmappedHotels.map(unmappedHotel => ({
    label: unmappedHotel.name,
    value: unmappedHotel.uuid,
  }));
  const unmappedHotelsRequest = useSelector(requestMappableStaysInternalGetSelector);
  const isUnmappedHotelsLoading = unmappedHotelsRequest === ENetworkRequestStatus.PENDING;

  const mappableStays = useSelector(mappableStaysSelector);
  const isMappableStaysTruncated = useSelector(isMappableStaysTruncatedSelector);
  const externalStayOptions = mappableStays.map(item => ({
    label: item.name + ' - ID ' + item.externalHotelId,
    value: item.externalHotelId,
  }));
  const mappableStaysRequest = useSelector(requestMappableStaysExternalGetSelector);
  const isMappableStaysLoading = mappableStaysRequest === ENetworkRequestStatus.PENDING;

  const patchMappableStaysRequest = useSelector(requestMappableStaysPatchSelector);
  const isSaving = patchMappableStaysRequest === ENetworkRequestStatus.PENDING;

  const requestInProgress = isUnmappedHotelsLoading || isMappableStaysLoading || isSaving;

  const mapStayModalContentClassname = classnames('map-stay-modal-content flex flex-col flex-1');
  
  useEffect(() => {
    dispatch(createActions.getMappableStaysInternalRequestAction());
  }, [dispatch]);

  const initialValues: IMapStayFormValues = {
    stayUuid: null,
    externalStayUuid: null,
    externalSupplier: null,
    termFilter: ''
  };

  const handleFormSubmit = useCallback(
    (mapStayFormValues: IMapStayFormValues, formikHelpers: FormikHelpers<IMapStayFormValues>) => {
      const { stayUuid, externalStayUuid, externalSupplier } = mapStayFormValues;
      if (!stayUuid || !externalStayUuid || !externalSupplier) {
        return;
      }
      dispatch(createActions.patchMappableStayRequestAction(stayUuid, externalStayUuid, externalSupplier));

      formikHelpers.setSubmitting(false);
    },
    [dispatch]
  );

  return (
    <GeneralModal
      onClose={onCancel}
      modalWindowClassName="flex justify-center max-w-[470px] px-35px py-25px rounded"
      shouldCloseByClickingOutside
    >
      <div className={classnames('map-stay-modal flex justify-center w-screen')}>
        <div className={mapStayModalContentClassname}>
          <h3 className="m-0 font-noe-display font-normal text-black text-[21px] leading-[27px]">
            New Mapping
          </h3>
          <p className="font-pt-sans text-15px leading-22px text-black mb-0 mt-25px">
            Select a Stay from the application and the External Supplier to provide Live Rates for it. 
            <br />
            <br />
            When selecting a Supplier you can then select one of their services to map the Stay to. In most cases the name matches, but there might be differences.
          </p>

          {isUnmappedHotelsLoading && (
            <div className="mt-5"><LoadingBar label="Loading..." /></div>
          )}
          {!isUnmappedHotelsLoading && (
            <div>
              <Formik
                initialValues={initialValues}
                validationSchema={MapStayFormValidationSchema}
                enableReinitialize={true}
                validateOnBlur={false}
                onSubmit={handleFormSubmit}
              >
                {(form: FormikProps<IMapStayFormValues>) => {
                  
                  const someFieldsAreEmpty = form.values.stayUuid === null
                    && form.values.externalStayUuid === null
                    && form.values.externalSupplier === null;

                  return (
                    <form onSubmit={form.handleSubmit}>
                      <div className="flex flex-col gap-[25px] mt-25px">
                        <Field name="stayUuid">
                          {({ field: { name, value }, form: { setFieldValue } }) => (
                            <SingleSelect
                              fieldId="stayUuid"
                              label="Stay"
                              className="stayUuid max-w-[400px]"
                              labelClassName="text-[16px] leading-[21px] font-bold"
                              value={value}
                              options={stayOptions}
                              onChange={value => {
                                setFieldValue(name, value);
                              }}
                              maxVisibleItems={6}
                              scrollToSelectedItem
                              errorMessage={
                                form.touched.stayUuid && form.errors.stayUuid ? form.errors.stayUuid : null
                              }
                              errorClassName="stayUuid-error"
                            />
                          )}
                        </Field>
                        <div className="flex gap-[10px]">
                          <Field name="externalSupplier">
                            {({ field: { name, value }, form: { setFieldValue } }) => (
                              <SingleSelect
                                fieldId="externalSupplier"
                                label="External Supplier"
                                className="externalSupplier flex-1 max-w-[400px]"
                                labelClassName="text-[16px] leading-[21px] font-bold"
                                value={value}
                                options={supplierOptions}
                                onChange={value => {
                                  setFieldValue(name, value);
                                }}
                                maxVisibleItems={4}
                                errorMessage={
                                  form.touched.externalSupplier && form.errors.externalSupplier ? form.errors.externalSupplier : null
                                }
                                errorClassName="externalSupplier-error"
                              />
                            )}
                          </Field>
                          <Field
                            as={TextInput}
                            type="text"
                            name="termFilter"
                            id="termFilter"
                            labelClassName="text-[16px] leading-[21px] font-bold"
                            className="termFilter flex-1"
                            label="External Name / ID"
                          />
                        </div>
                        <FluidButton
                          className="search"
                          type="primary"
                          onClick={() => dispatch(
                            createActions.getMappableStaysExternalRequestAction(
                              form.values.externalSupplier!,
                              form.values.termFilter
                            )
                          )}
                          isLoading={isMappableStaysLoading}
                          disabled={!form.values.externalSupplier || isMappableStaysLoading}
                        >
                          Search
                        </FluidButton>  
                        <Field name="externalStayUuid">
                          {({ field: { name, value }, form: { setFieldValue } }) => (
                            <div>
                              <RadioListBox
                              id="externalStayUuid"
                              label="External Stay"
                              value={value}
                              options={externalStayOptions}
                              textWhenEmpty={form.values.externalSupplier === null ? 'Select External Supplier.' : 'No External Stay found.'}
                              className="externalStayUuid max-w-[400px] h-[100px]"
                              labelClassName="text-[16px] leading-[21px] font-bold"
                              onChange={value => {
                                setFieldValue(name, value);
                              }}
                              errorMessage={
                                form.touched.externalStayUuid && form.errors.externalStayUuid ? form.errors.externalStayUuid : null
                              }
                              errorClassName="externalStayUuid-error"
                            />
                            {isMappableStaysTruncated && (
                              <p className="font-pt-sans text-15px leading-22px text-yellow-50 mb-0 mt-10px">
                                The list is too long. Adjust filtering to narrow down the results.
                              </p>
                            )}
                            </div>
                          )}
                        </Field>
                        <div className="map-stay-buttons flex gap-10px">
                          <FluidButton className="map-stay-cancel" type="secondary" onClick={onCancel}>
                            Cancel
                          </FluidButton>
                          <FluidButton
                            className="map-stay-save"
                            type="primary"
                            onClick={() => form.submitForm()}
                            isLoading={isSaving}
                            disabled={!form.isValid || someFieldsAreEmpty || requestInProgress}
                          >
                            Save
                          </FluidButton>
                        </div>
                      </div>
                    </form>
                  );
                }}
              </Formik>
            </div>
          )}
        </div>
      </div>
    </GeneralModal>
  );
};
